// Centralized TanStack Query key factories.
// Use these everywhere — never write raw key arrays inline.
//
// Hierarchy: domain → entity → identifier
//   qk.jobs.all()       → ['jobs']
//   qk.jobs.detail(id)  → ['job', id]
//   qk.calendar.assignments(from, to) → ['calendar', 'assignments', from, to]
//
// Why: invalidation patterns become unambiguous. If you invalidate
// `qk.jobs.all()` you only invalidate the jobs list. Invalidate `['jobs']`
// inline and someone next year wonders if it also nuked job details.

export const qk = {
  me: () => ['me'] as const,

  jobs: {
    all: () => ['jobs'] as const,
    detail: (id: string) => ['job', id] as const,
    documents: (id: string) => ['job-documents', id] as const,
    timeEntries: (id: string) => ['job-time-entries', id] as const,
    installAssignments: (id: string) => ['job-install-assignments', id] as const,
    templates: () => ['job-templates'] as const,
  },

  customers: {
    all: () => ['customers'] as const,
  },

  crews: {
    all: () => ['crews'] as const,
  },

  inventory: {
    // Active-only SKU rollup — the default everywhere (estimate picker, prefetch).
    skus: () => ['inventory', 'skus'] as const,
    // Includes disabled SKUs — ONLY the Inventory page (which partitions them
    // client-side). Distinct key so it can never pollute the active-only `skus()`
    // cache that the Estimate picker reads. Invalidating `skus()` still prefix-
    // matches this (['inventory','skus'] is a prefix of ['inventory','skus','all']).
    skusAll: () => ['inventory', 'skus', 'all'] as const,
    txns: (skuId: string) => ['inventory', 'txns', skuId] as const,
  },

  skus: {
    all: () => ['skus'] as const,
    // Raw catalog INCLUDING disabled SKUs — ONLY the Inventory page (joins them into
    // skuLookup so disabled rows show/edit cost+price). Distinct key so it never pollutes the
    // active-only catalog the Estimate picker reads. Invalidating all() (['skus']) still
    // prefix-matches this (['skus'] is a prefix of ['skus','all']).
    allWithInactive: () => ['skus', 'all'] as const,
    categories: () => ['sku-categories'] as const,
    // Distinct spec-field values for the New SKU form, scoped per category.
    specOptions: (categoryId: string) => ['skus', 'spec-options', categoryId] as const,
  },

  dashboard: {
    today: () => ['dashboard', 'today'] as const,
  },

  calendar: {
    all: () => ['calendar'] as const,
    assignments: (from: string, to: string) =>
      ['calendar', 'assignments', from, to] as const,
    assignmentsAll: () => ['calendar', 'assignments'] as const,
    // Sentinel "all dates" assignments query (2020–2030) shared by
    // JobScheduleSection + JobDetailPage. DISTINCT from assignmentsAll()
    // (the 2-element invalidation prefix) — never conflate the two.
    assignmentsAllDates: () => ['calendar', 'assignments', 'all'] as const,
    unassigned: () => ['calendar', 'unassigned'] as const,
  },

  settings: {
    nextPo: () => ['next-po'] as const,
  },

  financials: {
    year: (year: number) => ['financials', year] as const,
  },

  // Crew Utilization analytics — separate keys so the year pulldown only
  // refetches the chart series, not the period payload or a person drill.
  crewUtilization: {
    period: (weekFrom: string, weekTo: string, monthFrom: string, monthTo: string) =>
      ['crew-utilization', 'period', weekFrom, weekTo, monthFrom, monthTo] as const,
    series: (year: number) => ['crew-utilization', 'series', year] as const,
    person: (id: string, from: string, to: string) =>
      ['crew-utilization', 'person', id, from, to] as const,
  },
} as const;
